import { createContext, FC, ReactNode, useContext } from 'react';

type AnchorContextValue = {
  scrollToSection: (href: string) => void;
};

const AnchorContext = createContext<AnchorContextValue | null>(null);

/**
 * Hook to access anchor-related functionality (scroll to section, etc.)
 * Must be used within AnchorProvider
 */
export const useAnchor = () => {
  const context = useContext(AnchorContext);
  if (!context) {
    throw new Error('useAnchor must be used within AnchorProvider');
  }
  return context;
};

/**
 * Hook to safely access anchor functionality when it might not be available
 * Returns null if not within AnchorProvider
 */
export const useAnchorOptional = () => {
  return useContext(AnchorContext);
};

export type AnchorProviderProps = {
  children: ReactNode;
  scrollToSection: (href: string) => void;
};

export const AnchorProvider: FC<AnchorProviderProps> = ({
  children,
  scrollToSection,
}) => {
  const value: AnchorContextValue = {
    scrollToSection,
  };

  return (
    <AnchorContext.Provider value={value}>{children}</AnchorContext.Provider>
  );
};

AnchorProvider.displayName = 'AnchorProvider';
